import { useState } from "react";
import { H3, Label } from "../Text";
import { toast } from "react-toastify";
import Circle from "../Animation/Circle";
import { Button } from "../Buttons";
import { Request } from "../../hooks/Request";
import TableComplete from "../Tables/TableComplete";
import { requestAuth } from "../services/RequestService";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../Modal";
import CreatableSelect from "react-select/creatable";
import { validateArray } from "../../utils/validate";
import { FormDetailsDocument } from "./FormDetailsDocument";

const InputSubContaAddMulti = ({ field }) => {
  const { openDialog, dialogClose } = useDialog();

  const { data, loading, call } = Request({
    urlApi: `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
  });
  const {
    data: document,
    loading: loadDocument,
    call: callDocument,
  } = Request({
    urlApi: field.urlApi,
  });

  function handleForm(item) {
    openDialog(
      <>
        {loadDocument && (
          <div className="min-h-[360px]">
            <FormAddDocument
              data={document}
              item={item}
              reload={call}
              field={field}
              onClose={dialogClose}
              callDocument={callDocument}
            />
          </div>
        )}
      </>,
    );
  }

  function handleFormItems(item) {
    openDialog(
      <>
        {loadDocument && (
          <div className="">
            <FormDetailsDocument
              item={item}
              reload={call}
              onClose={dialogClose}
            />
          </div>
        )}
      </>,
    );
  }

  function handleDelete(data) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estas seguro que quieres eliminar este item?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/dataSet/procedure/subData/${data.id}`}
        texBtn="Eliminar"
        handleSuccess={call}
      />,
    );
  }

  return (
    <div className="bg-gray-300/40 p-2 rounded-md">
      <Label htmlFor={field.name} className={`${field.className} my-0`}>
        {field.labelCustom ? field.labelCustom : field.label}
      </Label>
      <div className="bg-gray-100 rounded-lg p-3 pt-2">
        {loadDocument && (
          <FormAddDocument
            data={document}
            reload={call}
            field={field}
            onClose={() => {}}
            callDocument={callDocument}
          />
        )}
      </div>
      <br />
      {!loading ? (
        <Circle />
      ) : (
        <div className="w-full">
          <TableComplete
            header={[
              {
                name: "label",
                label: "Campo",
              },
              {
                name: "observation",
                label: "Rangos",
                type: "custom",
                builder: (_, __, data) => {
                  try {
                    const ranges = JSON.parse(data.observation);
                    return (
                      <div className="text-sm">
                        {ranges.map((range, idx) => (
                          <div key={idx}>
                            {range.mode === "total" ? (
                              <div>Monto Total: {range.totalAmount}</div>
                            ) : (
                              <div>
                                Items {range.from}-{range.to}: {range.amount}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  } catch {
                    return <span>{data.observation}</span>;
                  }
                },
              },
              {
                name: "description2",
                label: "Total",
              },
              {
                name: "add",
                type: "action",
                label: "Opciones",
                sticky: true,
                actions: [
                  {
                    label: "Editar",
                    icon: "fas fa-edit",
                    action: handleForm,
                    color: "text-[#1d4ed8]",
                  },
                  {
                    label: "Eliminar",
                    icon: "fas fa-trash",
                    action: handleDelete,
                    color: "text-red-500",
                  },
                ],
              },
            ]}
            data={data}
          />
        </div>
      )}
    </div>
  );
};

const FormAddDocument = (props) => {
  const { data, item = null, reload, field, onClose, callDocument } = props;

  // Parsear datos existentes
  const parseObservation = (observationStr) => {
    try {
      const parsed = JSON.parse(observationStr);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const initialData = item?.observation
    ? parseObservation(item.observation)
    : [
        {
          mode: "ranges",
          from: 1,
          to: field.items?.length || 1,
          amount: "",
          totalAmount: "",
        },
      ];

  const isTotalMode = initialData.length > 0 && initialData[0].mode === "total";

  const [values, setValues] = useState({
    Label: item?.label ? item.label : validateArray(data) ? data[0].name : "",
    NumberValue: item?.numberValue ? item.numberValue : 0,
    Description: item?.description ? item.description : "",
    Description2: item?.description2 ? item.description2 : "",
    isNew: false,
  });

  const [mode, setMode] = useState(isTotalMode ? "total" : "ranges");
  const [ranges, setRanges] = useState(
    mode === "ranges"
      ? initialData
      : [{ mode: "ranges", from: 1, to: field.items?.length || 1, amount: "" }],
  );
  const [totalAmount, setTotalAmount] = useState(
    mode === "total" && initialData.length > 0
      ? initialData[0].totalAmount || ""
      : "",
  );

  const calculateTotal = () => {
    if (mode === "total") {
      return parseFloat(totalAmount) || 0;
    } else {
      return ranges.reduce((total, range) => {
        const from = parseInt(range.from) || 0;
        const to = parseInt(range.to) || 0;
        const amount = parseFloat(range.amount) || 0;
        const count = Math.max(0, to - from + 1);
        return total + count * amount;
      }, 0);
    }
  };

  const addRange = () => {
    const lastRange = ranges[ranges.length - 1];
    const nextFrom = lastRange ? parseInt(lastRange.to) + 1 : 1;
    const totalItems = field.items?.length || 1;

    setRanges([
      ...ranges,
      {
        mode: "ranges",
        from: nextFrom,
        to: Math.min(nextFrom, totalItems),
        amount: "",
      },
    ]);
  };

  const removeRange = (index) => {
    if (ranges.length > 1) {
      setRanges(ranges.filter((_, idx) => idx !== index));
    }
  };

  const updateRange = (index, field, value) => {
    const newRanges = [...ranges];
    newRanges[index] = {
      ...newRanges[index],
      [field]: value,
    };
    setRanges(newRanges);
  };

  const validateForm = () => {
    if (!values.Label) {
      toast.error("Debe seleccionar un campo");
      return false;
    }

    if (mode === "total") {
      if (!totalAmount || isNaN(parseFloat(totalAmount))) {
        toast.error("Debe ingresar un monto total válido");
        return false;
      }
      return true;
    }

    const totalItems = field.items?.length || 0;

    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i];
      const from = parseInt(range.from);
      const to = parseInt(range.to);
      const amount = parseFloat(range.amount);

      if (isNaN(from) || isNaN(to) || isNaN(amount)) {
        toast.error(
          `Rango ${i + 1}: Todos los campos deben ser números válidos`,
        );
        return false;
      }

      if (from < 1 || to < 1) {
        toast.error(`Rango ${i + 1}: Los valores deben ser mayores a 0`);
        return false;
      }

      if (from > to) {
        toast.error(
          `Rango ${i + 1}: El inicio no puede ser mayor que el final`,
        );
        return false;
      }

      if (to > totalItems) {
        toast.error(
          `Rango ${i + 1}: El final (${to}) no puede ser mayor que el total de items (${totalItems})`,
        );
        return false;
      }

      for (let j = 0; j < ranges.length; j++) {
        if (i !== j) {
          const otherFrom = parseInt(ranges[j].from);
          const otherTo = parseInt(ranges[j].to);

          if (
            (from >= otherFrom && from <= otherTo) ||
            (to >= otherFrom && to <= otherTo) ||
            (from <= otherFrom && to >= otherTo)
          ) {
            toast.error(`Los rangos ${i + 1} y ${j + 1} se solapan`);
            return false;
          }
        }
      }
    }

    return true;
  };

  async function handleSubmit() {
    if (!validateForm()) {
      return;
    }

    if (values.isNew) {
      await handleCreateOption(values);
    }

    const account = data.filter((item) => item.name === values.Label)[0];
    const total = calculateTotal();

    let observationData;
    if (mode === "total") {
      observationData = [{ mode: "total", totalAmount }];
    } else {
      observationData = ranges.map((range) => ({
        mode: "ranges",
        from: range.from,
        to: range.to,
        amount: range.amount,
      }));
    }

    await requestAuth(
      item ? "put" : "post",
      item
        ? `/dataSet/procedure/subData/${item.id}`
        : `/dataSet/procedure/${field.id}/field/${field.fieldId}/subdata`,
      {
        ...values,
        description2: total.toString(),
        description3: account?.data || "",
        description4: "true",
        observation: JSON.stringify(observationData),
      },
    )
      .then(() => {
        reload();
        onClose();
        setValues({
          ...values,
          NumberValue: 0,
          Description: "",
          Description2: "",
        });
        setRanges([
          { mode: "ranges", from: 1, to: field.items?.length || 1, amount: "" },
        ]);
        setTotalAmount("");
        toast.success("Registro exitoso");
      })
      .catch((error) => {
        console.error(error);
        toast.error("Se produjo un error al guardar");
      });
  }

  async function handleCreateOption(value) {
    await requestAuth("post", "/Dropdown/Option", {
      Name: value.Label,
      DropdownListLabel: field.urlInitial,
    }).then(() => {
      callDocument();
    });
  }

  function handleChangeInp(e) {
    const { name, value } = e.target;
    setValues((a) => ({
      ...a,
      [name]: value,
    }));
  }

  function handleChange(e) {
    setValues((a) => ({
      ...a,
      Label: e.value,
      isNew: e.__isNew__ || false,
    }));
  }

  const currentTotal = calculateTotal();

  return (
    <form className="flex flex-col">
      <Label className={`mt-2`}>Lista de campos</Label>
      {validateArray(data) ? (
        <CreatableSelect
          isSearchable={true}
          onChange={handleChange}
          value={{ value: values.Label, label: values.Label }}
          styles={{
            placeholder: (base) => ({
              ...base,
              color: "black",
            }),
          }}
          placeholder={"Seleccionar..."}
          isOptionSelected={false}
          options={data.map((option) => ({
            value: option.name,
            label: option.name,
          }))}
        />
      ) : null}

      <div className="mt-4 bg-white p-3 rounded-md">
        <div className="mb-4">
          <Label className="mb-2">Seleccione el modo de cálculo:</Label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="mode"
                value="ranges"
                checked={mode === "ranges"}
                onChange={(e) => setMode(e.target.value)}
                className="mr-2"
              />
              <span>Por Rangos</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="mode"
                value="total"
                checked={mode === "total"}
                onChange={(e) => setMode(e.target.value)}
                className="mr-2"
              />
              <span>Monto Total</span>
            </label>
          </div>
        </div>

        {mode === "ranges" ? (
          <>
            <div className="flex justify-between items-center mb-3">
              <Label className="my-0">
                Gestión de Rangos (Total items: {field.items?.length || 0})
              </Label>
              <Button
                type="button"
                onClick={addRange}
                className="px-3 py-1 text-sm"
              >
                <i className="fas fa-plus mr-1"></i> Agregar Rango
              </Button>
            </div>

            {ranges.map((range, index) => (
              <div
                key={index}
                className="mb-3 p-3 bg-gray-50 rounded-md border"
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-sm">
                    Rango {index + 1}
                  </span>
                  {ranges.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRange(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs my-1">Desde</Label>
                    <input
                      type="number"
                      value={range.from}
                      onChange={(e) =>
                        updateRange(index, "from", e.target.value)
                      }
                      className="w-full p-2 rounded-md text-sm"
                      min="1"
                      max={field.items?.length || 1}
                    />
                  </div>
                  <div>
                    <Label className="text-xs my-1">Hasta</Label>
                    <input
                      type="number"
                      value={range.to}
                      onChange={(e) => updateRange(index, "to", e.target.value)}
                      className="w-full p-2 rounded-md text-sm"
                      min="1"
                      max={field.items?.length || 1}
                    />
                  </div>
                  <div>
                    <Label className="text-xs my-1">Monto</Label>
                    <input
                      type="number"
                      step="0.01"
                      value={range.amount}
                      onChange={(e) =>
                        updateRange(index, "amount", e.target.value)
                      }
                      className="w-full p-2 rounded-md text-sm"
                      min="0"
                    />
                  </div>
                </div>

                <div className="mt-2 text-xs text-gray-600">
                  Items:{" "}
                  {Math.max(
                    0,
                    (parseInt(range.to) || 0) - (parseInt(range.from) || 0) + 1,
                  )}{" "}
                  × {parseFloat(range.amount) || 0} =
                  {(
                    Math.max(
                      0,
                      (parseInt(range.to) || 0) -
                        (parseInt(range.from) || 0) +
                        1,
                    ) * (parseFloat(range.amount) || 0)
                  ).toFixed(2)}
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="mb-4 p-3 bg-gray-50 rounded-md border">
            <Label className="text-sm font-semibold mb-2">Monto Total</Label>
            <div className="flex items-center">
              <span className="mr-2 text-lg">$</span>
              <input
                type="number"
                step="0.01"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                className="w-full p-2 rounded-md text-lg"
                min="0"
                placeholder="Ingrese el monto total"
              />
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Este monto se aplicará directamente sin distribución por rangos.
            </div>
          </div>
        )}

        <div className="mt-3 p-2 bg-blue-50 rounded-md">
          <Label className="my-0 text-sm font-bold">
            Total General: {currentTotal.toFixed(2)}
          </Label>
          <div className="text-xs text-gray-600 mt-1">
            {mode === "total"
              ? "Modo: Monto Total Directo"
              : `Modo: Distribución por Rangos (${ranges.length} rango${ranges.length !== 1 ? "s" : ""})`}
          </div>
        </div>
      </div>

      <Button type="button" onClick={handleSubmit} className="px-3 py-3 mt-4">
        {item ? "Actualizar" : "Guardar"}
      </Button>
    </form>
  );
};

export default InputSubContaAddMulti;
